/**
 * Runtime config validation — fail fast when required env vars are missing.
 *
 * SAFE TO EXPOSE: All VITE_ vars are compiled into the client bundle.
 * They must only contain publishable/anon keys, never private secrets.
 *
 * Called once at boot. Throws if critical config is missing so the
 * pre-React crash screen can display a clear message.
 */

interface ConfigCheck {
  name: string;
  value: string | undefined;
  required: boolean;
  hint: string;
}

export function validateConfig(): void {
  const checks: ConfigCheck[] = [
    {
      name: "VITE_SUPABASE_URL",
      value: import.meta.env.VITE_SUPABASE_URL,
      required: true,
      hint: "Backend URL is missing. Check .env or Lovable Cloud config.",
    },
    {
      name: "VITE_SUPABASE_PUBLISHABLE_KEY",
      value: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
      required: true,
      hint: "Backend anon key is missing. Check .env or Lovable Cloud config.",
    },
  ];

  const missing = checks.filter((c) => c.required && !c.value?.trim());

  if (missing.length > 0) {
    const details = missing
      .map((m) => `  • ${m.name}: ${m.hint}`)
      .join("\n");
    throw new Error(
      `[Config] Missing required environment variables:\n${details}\n\nSee .env.example for setup instructions.`
    );
  }

  // Warn about optional config that affects features
  const optionalChecks: ConfigCheck[] = [
    {
      name: "VITE_FIREBASE_API_KEY",
      value: import.meta.env.VITE_FIREBASE_API_KEY,
      required: false,
      hint: "Web push notifications will be disabled.",
    },
    {
      name: "VITE_GOOGLE_MAPS_API_KEY",
      value: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
      required: false,
      hint: "Client-side maps fallback key not set — will rely on edge function.",
    },
  ];

  for (const check of optionalChecks) {
    if (!check.value?.trim()) {
      console.info(`[Config] ${check.name} not set — ${check.hint}`);
    }
  }
}
